import * as React from "react";

import { useMerchantModel } from "client/hooks";
import type { MerchantCollections } from "client/lib/apt-merchant";

import type {
  FormCollectionState,
  ModalMode,
  ModalState,
} from "./FormCollectionTypes";

export const FormCollectionContext = React.createContext<
  FormCollectionState | undefined
>(undefined);

export type FormCollectionInitialState = {
  collection: MerchantCollections["locations"] | MerchantCollections["owners"];
} & Partial<FormCollectionState>;

export const useFormCollection = (): FormCollectionState => {
  const ctx = React.useContext(FormCollectionContext);

  if (ctx === undefined) {
    throw new Error(
      `useFormCollection must be used within a FormCollectionContext provider`
    );
  }
  return ctx;
};

export const FormCollectionProvider: React.FC<{
  initialState: FormCollectionInitialState;
}> = ({ children, initialState }) => {
  const {
    addFieldSetToCollection,
    removeFieldSetFromCollection,
  } = useMerchantModel();
  const [canAddToCollection, setCanAddToCollection] = React.useState(
    initialState.canAddToCollection || false
  );
  const [completedFieldSetCount, setCompletedFieldSetCount] = React.useState(
    initialState.completedFieldSetCount || 1
  );
  const [currentFieldSetIndex, setCurrentFieldSetIndex] = React.useState(
    initialState.currentFieldSetIndex || 0
  );
  const [modalState, setModalState] = React.useState<ModalState>(
    initialState.modalState ?? "closed"
  );
  const [modalMode, setModalMode] = React.useState<ModalMode>(
    initialState.modalMode ?? "add"
  );

  const handleAddToCollection = (collectionType: keyof MerchantCollections) => {
    addFieldSetToCollection(collectionType);
    setModalMode("add");
    setModalState("open");
    setCurrentFieldSetIndex(completedFieldSetCount);
  };

  const handleRemoveFromCollection = (
    collectionType: keyof MerchantCollections,
    index: number
  ) => {
    removeFieldSetFromCollection(collectionType, index);
    setModalState("closed");
    setCurrentFieldSetIndex(0);
  };

  React.useEffect(() => {
    if (completedFieldSetCount !== initialState.collection.length) {
      setCompletedFieldSetCount(initialState.collection.length);
    }
  }, [completedFieldSetCount, initialState.collection.length]);

  const value = {
    canAddToCollection,
    completedFieldSetCount,
    currentFieldSetIndex,
    handleAddToCollection,
    handleRemoveFromCollection,
    modalMode,
    modalState,
    setCanAddToCollection,
    setCompletedFieldSetCount,
    setCurrentFieldSetIndex,
    setModalMode,
    setModalState,
  };
  return (
    <FormCollectionContext.Provider value={value}>
      {children}
    </FormCollectionContext.Provider>
  );
};
